import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  FileClock,
  GitCompareArrows,
  Menu,
  Network,
  LogOut,
  ShieldCheck,
  SlidersHorizontal,
  TableProperties,
  UploadCloud,
  X,
} from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import {
  AppContext,
  type DepartmentOption,
  type SnapshotTerm,
} from "../context/AppContext";
import { ApiRequestError, apiFetch } from "../lib/api";
import type { AuthenticatedUser } from "../shared/schemas";
import { ErrorState, LoadingState } from "./ui";

const primaryNavigation = [
  { to: "/", label: "Dashboard", icon: BarChart3 },
  { to: "/timetable", label: "Timetable", icon: CalendarDays },
  { to: "/schedule-builder", label: "Schedule Builder", icon: CalendarPlus },
  { to: "/rooms", label: "Rooms", icon: Building2 },
  { to: "/cross-lists", label: "Cross-Lists", icon: Network },
  { to: "/bottlenecks", label: "Bottlenecks", icon: AlertTriangle },
  { to: "/sections", label: "Sections", icon: TableProperties },
];

const workflowNavigation = [
  { to: "/imports", label: "Import History", icon: UploadCloud },
  { to: "/comparisons", label: "Comparisons", icon: GitCompareArrows },
  { to: "/scenarios", label: "Scenarios", icon: SlidersHorizontal },
  { to: "/alerts", label: "Alerts", icon: Bell },
];

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [snapshotTermId, setSnapshotTermId] = useState(
    () => localStorage.getItem("fbit.snapshotTermId") ?? "",
  );
  const [departmentId, setDepartmentId] = useState(
    () => localStorage.getItem("fbit.departmentId") ?? "",
  );
  const userQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => apiFetch<AuthenticatedUser>("/me"),
  });
  const termsQuery = useQuery({
    queryKey: ["terms"],
    queryFn: () => apiFetch<SnapshotTerm[]>("/terms"),
    enabled: userQuery.isSuccess,
  });
  const departmentsQuery = useQuery({
    queryKey: ["departments", snapshotTermId],
    queryFn: () =>
      apiFetch<DepartmentOption[]>(
        `/departments?${new URLSearchParams({ snapshotTermId }).toString()}`,
      ),
    enabled: userQuery.isSuccess && Boolean(snapshotTermId),
  });

  useEffect(() => {
    if (!snapshotTermId && termsQuery.data?.length) {
      const active = termsQuery.data.find((term) => term.isActive) ?? termsQuery.data[0];
      setSnapshotTermId(active.id);
    }
  }, [snapshotTermId, termsQuery.data]);

  useEffect(() => {
    if (snapshotTermId) localStorage.setItem("fbit.snapshotTermId", snapshotTermId);
  }, [snapshotTermId]);

  useEffect(() => {
    if (
      departmentId &&
      departmentsQuery.data &&
      !departmentsQuery.data.some((department) => department.code === departmentId)
    ) {
      setDepartmentId("");
    }
  }, [departmentId, departmentsQuery.data]);

  useEffect(() => {
    if (departmentId) localStorage.setItem("fbit.departmentId", departmentId);
    else localStorage.removeItem("fbit.departmentId");
  }, [departmentId]);

  const contextValue = useMemo(
    () =>
      userQuery.data && termsQuery.data
        ? {
            user: userQuery.data,
            terms: termsQuery.data,
            snapshotTermId,
            setSnapshotTermId,
            departments: departmentsQuery.data ?? [],
            departmentId,
            setDepartmentId,
          }
        : null,
    [
      departmentId,
      departmentsQuery.data,
      snapshotTermId,
      termsQuery.data,
      userQuery.data,
    ],
  );

  if (userQuery.isLoading || termsQuery.isLoading) {
    return <div className="p-6"><LoadingState label="Authenticating and loading snapshots..." /></div>;
  }
  if (userQuery.error instanceof ApiRequestError && userQuery.error.status === 401) {
    return <PasscodeLogin onSuccess={() => void userQuery.refetch()} />;
  }
  if (userQuery.error || termsQuery.error || !contextValue) {
    return <div className="p-6"><ErrorState error={userQuery.error ?? termsQuery.error} /></div>;
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition ${
      isActive
        ? "bg-brand-600 text-white shadow-sm"
        : "text-slate-300 hover:bg-white/10 hover:text-white"
    }`;

  const sidebar = (
    <>
      <div className="flex h-20 items-center gap-3 border-b border-white/10 px-5">
        <div className="rounded-xl bg-brand-600 p-2.5 shadow-lg shadow-brand-950/30">
          <CalendarDays className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="font-black leading-tight text-white">FBIT Scheduling</p>
          <p className="text-xs text-indigo-300">Operational Visualizer</p>
        </div>
      </div>
      <nav className="flex-1 space-y-6 overflow-y-auto p-4">
        <div className="space-y-1">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Analytics
          </p>
          {primaryNavigation.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={navLinkClass} onClick={() => setMobileOpen(false)}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
        <div className="space-y-1">
          <p className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Workflows
          </p>
          {workflowNavigation.map((item) => (
            <NavLink key={item.to} to={item.to} className={navLinkClass} onClick={() => setMobileOpen(false)}>
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
          {contextValue.user.role === "administrator" && (
            <NavLink to="/admin" className={navLinkClass} onClick={() => setMobileOpen(false)}>
              <ShieldCheck className="h-4 w-4" />
              Administration
            </NavLink>
          )}
        </div>
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="rounded-xl bg-white/5 p-3">
          <p className="truncate text-sm font-semibold text-white">{contextValue.user.displayName}</p>
          <p className="truncate text-xs text-slate-400">{contextValue.user.email}</p>
          <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-indigo-300">
            {contextValue.user.role.replace("_", " ")}
          </p>
          <button
            className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-white"
            type="button"
            onClick={() => {
              void apiFetch<void>("/auth/logout", { method: "POST" }).finally(() => {
                window.location.reload();
              });
            }}
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </div>
    </>
  );

  return (
    <AppContext.Provider value={contextValue}>
      <div className="min-h-screen bg-slate-50">
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-slate-950 lg:flex">
          {sidebar}
        </aside>
        {mobileOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button className="absolute inset-0 bg-slate-950/70" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />
            <aside className="relative flex h-full w-72 flex-col bg-slate-950">
              <button className="absolute right-3 top-3 rounded-lg p-2 text-slate-300 hover:bg-white/10" onClick={() => setMobileOpen(false)} aria-label="Close navigation">
                <X className="h-5 w-5" />
              </button>
              {sidebar}
            </aside>
          </div>
        )}
        <div className="lg:pl-64">
          <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
            <div className="flex h-16 items-center gap-4 px-4 sm:px-6 lg:px-8">
              <button className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
                <Menu className="h-5 w-5" />
              </button>
              <div className="ml-auto flex items-center gap-3">
                <label className="hidden text-xs font-bold uppercase tracking-wider text-slate-400 sm:block" htmlFor="department-selector">
                  Department
                </label>
                <div className="relative">
                  <select
                    id="department-selector"
                    className="max-w-44 appearance-none rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-9 text-sm font-semibold text-slate-700"
                    value={departmentId}
                    onChange={(event) => setDepartmentId(event.target.value)}
                    disabled={!snapshotTermId || departmentsQuery.isLoading}
                  >
                    <option value="">All departments</option>
                    {(departmentsQuery.data ?? []).map((department) => (
                      <option key={department.code} value={department.code}>
                        {department.code} ({department.sectionCount})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
                </div>
                <FileClock className="hidden h-4 w-4 text-slate-400 sm:block" />
                <label className="sr-only" htmlFor="snapshot-selector">Active snapshot</label>
                <div className="relative">
                  <select
                    id="snapshot-selector"
                    className="appearance-none rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-9 text-sm font-semibold text-slate-700"
                    value={snapshotTermId}
                    onChange={(event) => setSnapshotTermId(event.target.value)}
                  >
                    {contextValue.terms.length === 0 && <option value="">No snapshots</option>}
                    {contextValue.terms.map((term) => (
                      <option key={term.id} value={term.id}>
                        {term.displayName} / {term.termCode} {term.isActive ? "(Active)" : `(${term.status})`}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>
            </div>
          </header>
          <main className="mx-auto max-w-[1600px] space-y-6 p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </AppContext.Provider>
  );
}

function PasscodeLogin({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await apiFetch<{ success: true }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setPassword("");
      onSuccess();
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Unable to sign in.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-xl bg-brand-600 p-2.5">
            <CalendarDays className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-black text-slate-950">FBIT Scheduling</h1>
            <p className="text-sm text-slate-500">Sign in to the scheduling visualizer.</p>
          </div>
        </div>
        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="app-email">
              Email
            </label>
            <input
              id="app-email"
              className="field w-full"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="app-passcode">
              Passcode
            </label>
            <input
              id="app-passcode"
              className="field w-full"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>
          {error && <p className="text-sm font-medium text-rose-700">{error}</p>}
          <button className="button-primary w-full" type="submit" disabled={submitting}>
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
